// ANGULAR
import { FormGroup } from '@angular/forms';
import {
  Component,
  ChangeDetectorRef,
  DestroyRef,
  ViewChild,
  effect,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { merge } from 'rxjs';

// SERVICES
import { DataModelHelperService } from 'src/app/app-services-helper/data-model-helper.service';

// HELPER
import {
  haversineDistance,
  WHIconENUM,
  IWHUIConfigModel,
  IWHLocationDTO,
  WHNgxDndDraggableModel,
  WHToastService,
  WHMetadataDataService,
} from '@workheld/workheld-shared-lib';
import { WHDndBehaviorSubjectHelperService } from 'src/app/app-services-helper/w-h-dnd-behavior-subject-helper.service';

import { GoogleMap, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { GoogleMapsLoaderService } from 'src/app/app-services/google-maps.service';

@Component({
  selector: 'ng-bee-w-h-agm-core-location-form',
  templateUrl: './w-h-agm-core-location-form.component.html',
  styleUrls: ['./w-h-agm-core-location-form.component.scss'],
  standalone: false,
})
export class WHAgmCoreLocationFormComponent {
  private readonly metadataDataService = inject(WHMetadataDataService);
  private readonly ngxToastrService = inject(WHToastService);
  private readonly dataModelHelperService = inject(DataModelHelperService);
  private readonly dndBehaviorSubjectHelperService = inject(
    WHDndBehaviorSubjectHelperService,
  );
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  public readonly googleMapsService = inject(GoogleMapsLoaderService);
  private readonly destroyRef = inject(DestroyRef);

  // ENUMS
  public COUNTRY: IWHUIConfigModel[] = [];
  public supportedCountryIsoEnums: string[] = [];

  // INPUTS
  public readonly locationFormGroup = input.required<FormGroup>();
  public readonly supportDragNDrop = input<boolean>(false);
  public readonly searchTabIndex = input<number>(0);
  public readonly formattedAddressTabIndex = input<number>(0);

  public draggable: WHNgxDndDraggableModel;

  // VIEW CHILDREN
  public readonly map = viewChild(GoogleMap);
  @ViewChild(MapInfoWindow) infoWindow: MapInfoWindow;
  @ViewChild(MapMarker) marker: MapMarker;

  // VAR
  public zoom: number = 15;
  private readonly defaultLatitude: number = 48.2081743;
  private readonly defaultLongitude: number = 16.37381890000006;

  // Bumped on every new reverse-geocode / marker-drag so late resolves
  // from a previous interaction can detect they are stale.
  private geocodeGeneration = 0;

  constructor() {
    // Configure the map once the ViewChild resolves.
    effect(() => {
      const googleMap = this.map()?.googleMap;
      if (googleMap) {
        googleMap.setOptions({
          streetViewControl: false,
          mapTypeControl: false,
        });
      }
    });

    this.dndBehaviorSubjectHelperService.dndBehaviorSubject
      .pipe(takeUntilDestroyed())
      .subscribe((draggable: WHNgxDndDraggableModel) => {
        if (this.supportDragNDrop()) {
          this.draggable = draggable;
        }
      });

    this.metadataDataService.metadataTranslation$
      .pipe(takeUntilDestroyed())
      .subscribe((metadataTranslation) => {
        this.COUNTRY = metadataTranslation.COUNTRY ?? [];
      });

    this.metadataDataService.metadataDOM$
      .pipe(takeUntilDestroyed())
      .subscribe((metadataDOM) => {
        this.supportedCountryIsoEnums = metadataDOM?.countries ?? [];
      });

    // Wait for the required input, then wire the control-level subscriptions
    // once (the guard prevents re-subscribing if the signal ever re-emits).
    let controlsWired = false;
    effect(() => {
      const formGroup = this.locationFormGroup();
      if (!formGroup || controlsWired) {
        return;
      }
      controlsWired = true;

      // If the user hand-edits the address or country, the previous 9 m
      // geocode match no longer holds — drop `exact` back to false.
      const formattedAddress = formGroup.controls['formattedAddress'];
      const countryCodeISO = formGroup.controls['countryCodeISO'];
      merge(formattedAddress.valueChanges, countryCodeISO.valueChanges)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (
            (formattedAddress.dirty || countryCodeISO.dirty) &&
            formGroup.controls['exact'].value
          ) {
            formGroup.controls['exact'].patchValue(false);
          }
        });

      formGroup.controls['exact'].valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((value: boolean) => {
          if (value) {
            this.infoWindow?.close();
          } else {
            this.infoWindow?.open(this.marker, false);
          }
        });
    });
  }

  // SEARCH SELECTION — same downstream logic (country check + form patch)
  // as every other place-resolution entry point in this component.
  public onPlaceSelected(place: google.maps.places.Place): void {
    const locationDTO: IWHLocationDTO =
      this.dataModelHelperService.initLocationDtoFromGooglePlace(place);

    const supportedCountryISO = this.getCountryISO(
      locationDTO.countryCodeISO?.toLowerCase(),
    );

    if (!supportedCountryISO) {
      this.sendWarningMessage('ERROR.COUNTRIES_NOT_SET');
      return;
    }

    this.placeChanged(locationDTO);

    // The direct Places API often returns no Name for plain addresses. Fall
    // back to the coordinate-based Name resolution used by map clicks and
    // pin drags so both entry points converge on the same Name rule.
    if (!locationDTO.name && place.location) {
      this.resolveNameByCoords({
        lat: place.location.lat(),
        lng: place.location.lng(),
      });
    }
  }

  // Runs only the Name-resolution half of the reverse-geocode pipeline
  // (geocode coords → filter POI candidates → fetchFields → Name rule),
  // leaving the caller's already-patched address/country/coordinates alone.
  private resolveNameByCoords(coords: { lat: number; lng: number }): void {
    const generation = ++this.geocodeGeneration;
    const latlng = new google.maps.LatLng(coords.lat, coords.lng);
    this.googleMapsService
      .loadGeocoding()
      .then(({ Geocoder }) => {
        if (generation !== this.geocodeGeneration) {
          return;
        }
        new Geocoder().geocode(
          { location: latlng },
          (results: google.maps.GeocoderResult[], status) => {
            if (generation !== this.geocodeGeneration) {
              return;
            }
            if (status !== 'OK' || !results?.length) {
              return;
            }
            this.resolveAndPatchPlaceName(results, generation);
          },
        );
      })
      .catch((error) => {
        console.log('Failed to load geocoding library', error);
      });
  }

  // USING PLACE in GOOGLE MAPS
  public placeChanged($event: IWHLocationDTO) {
    const formGroup = this.locationFormGroup();
    const locationDTO: IWHLocationDTO = $event;
    const locationSupplement: IWHLocationDTO = formGroup.controls[
      'locationSupplement'
    ].value as IWHLocationDTO;
    formGroup.reset();
    formGroup.patchValue(locationDTO);
    formGroup.controls['locationSupplement'].patchValue(locationSupplement);

    this.changeDetectorRef.detectChanges();
    this.updateInfoWindow();
    this.map()?.googleMap.setCenter({
      lat: this.latitude,
      lng: this.longitude,
    });
  }

  // Info window (map-info-window) is opened imperatively via Google's API.
  // Called after every write path that touches `exact` so it stays in sync.
  private updateInfoWindow(): void {
    if (!this.infoWindow || !this.marker) {
      return;
    }
    if (this.isExact) {
      this.infoWindow.close();
    } else {
      this.infoWindow.open(this.marker, false);
    }
  }

  // DRAG PIN — full update (address, country, exact, name) like a map click.
  public markerDragEnd(event: google.maps.MapMouseEvent) {
    const coords = event.latLng.toJSON();
    const formGroup = this.locationFormGroup();
    formGroup.controls['latitude'].patchValue(coords.lat);
    formGroup.controls['longitude'].patchValue(coords.lng);
    this.reverseGeocode(coords);
  }

  private getLocation(results: google.maps.GeocoderResult[]): IWHLocationDTO {
    return this.dataModelHelperService.initLocationDtoFromGoogleGeocoderResultArray(
      results,
    );
  }

  // Hard cap so a dense area with many geocoder results can never fan out
  // into more than a couple of paid Places Details lookups.
  private static readonly MAX_NAME_LOOKUPS = 2;
  private static readonly NAMED_PLACE_TYPES = new Set([
    'establishment',
    'point_of_interest',
  ]);

  // Filters the geocoder results to those whose own `types` metadata already
  // suggests a named POI (matching the Name rule) and stops at the first
  // Place whose displayName survives that rule after `fetchFields`.
  // `generation` allows an in-flight lookup to abort if a newer
  // reverse-geocode has started in the meantime.
  private async resolveAndPatchPlaceName(
    results: google.maps.GeocoderResult[],
    generation: number,
  ): Promise<void> {
        console.log('Resolving and patching place name for generation', results);

    const isStale = () => generation !== this.geocodeGeneration;
    const patchNameSafely = (name: string | null): void => {
      if (isStale()) {
        return;
      }
      console.log('Patching name safely with', name);
      this.locationFormGroup().controls['name'].patchValue(name);
      this.changeDetectorRef.detectChanges();
    };

    const candidates = results
      .filter(
        (result) =>
          result?.place_id &&
          result.types?.some((type) =>
            WHAgmCoreLocationFormComponent.NAMED_PLACE_TYPES.has(type),
          ),
      )
      .slice(0, WHAgmCoreLocationFormComponent.MAX_NAME_LOOKUPS);

    if (!candidates.length) {
      patchNameSafely(null);
      return;
    }

    try {
      const placesLib = (await google.maps.importLibrary(
        'places',
      )) as google.maps.PlacesLibrary;

      for (const candidate of candidates) {
        if (isStale()) {
          return;
        }
        try {
          const place = new placesLib.Place({ id: candidate.place_id });
          await place.fetchFields({ fields: ['displayName', 'types'] });
          if (isStale()) {
            return;
          }
          const name = this.dataModelHelperService.extractPlaceName(
            place.displayName,
            place.types,
          );
          if (name) {
            patchNameSafely(name);
            return;
          }
        } catch (innerError) {
          console.log(
            'Place fetch failed for',
            candidate.place_id,
            innerError,
          );
        }
      }

      patchNameSafely(null);
    } catch (error) {
      console.log('Failed to resolve place name', error);
      patchNameSafely(null);
    }
  }

  private getCountryISO(countryCodeISO: string | undefined | null) {
    if (!countryCodeISO) {
      return undefined;
    }
    const target = countryCodeISO.toLowerCase();
    return this.supportedCountryIsoEnums.find(
      (countryISO: string) => countryISO.toLowerCase() === target,
    );
  }

  public geoLocationSearch() {
    if (!this.isGeoSet) {
      return;
    }
    const fakeany: any = {
      lat: this.latitude,
      lng: this.longitude,
    };
    this.reverseGeocode(fakeany);
  }

  // REVERSE GEOCODE
  private reverseGeocode(coords: any) {
    if (!navigator.geolocation) {
      return;
    }
    const generation = ++this.geocodeGeneration;
    const latlng = new google.maps.LatLng(coords.lat, coords.lng);
    this.googleMapsService
      .loadGeocoding()
      .then(({ Geocoder }) => {
        if (generation !== this.geocodeGeneration) {
          return;
        }
        const geocoder = new Geocoder();
        geocoder.geocode(
          { location: latlng },
          (results: google.maps.GeocoderResult[], status) => {
            if (generation !== this.geocodeGeneration) {
              return;
            }
            if (status !== 'OK' || !results?.length) {
              return;
            }
            const formGroup = this.locationFormGroup();
            const locationDTO: IWHLocationDTO = this.getLocation(results);

            const supportedCountryISO: string = this.getCountryISO(
              locationDTO.countryCodeISO?.toLowerCase(),
            );

            if (!supportedCountryISO) {
              this.sendWarningMessage('ERROR.COUNTRIES_NOT_SET');
              return;
            }

            const isLocationExact: boolean = this.setExactAddress(
              locationDTO,
              coords,
            );

            formGroup.controls['latitude'].patchValue(coords.lat);
            formGroup.controls['longitude'].patchValue(coords.lng);
            formGroup.controls['formattedAddress'].patchValue(
              locationDTO.formattedAddress,
            );
            formGroup.controls['countryCodeISO'].patchValue(
              locationDTO.countryCodeISO,
            );

            if (!isLocationExact) {
              this.ngxToastrService.info(
                'clienterror.location.notexact.notification',
              );
            }

            formGroup.controls['exact'].patchValue(isLocationExact);
            this.changeDetectorRef.detectChanges();
            this.updateInfoWindow();

            // WHP-12496 / WHP-2750: fill the Name from the geocoded place
            // via the new Places API (was hard-reset to null before).
            this.resolveAndPatchPlaceName(results, generation);
          },
        );
      })
      .catch((error) => {
        console.log('Failed to load geocoding library', error);
      });
    this.map()?.googleMap.setCenter({
      lat: this.latitude,
      lng: this.longitude,
    });
  }

  center: google.maps.LatLngLiteral = {
    lat: 24,
    lng: 12,
  };
  markerOptions: google.maps.MarkerOptions = {
    draggable: true,
  };
  markerPositions: google.maps.LatLngLiteral[] = [];
  addMarker(event: google.maps.MapMouseEvent) {
    const coords = event.latLng.toJSON();
    if (event.latLng != null) this.markerPositions.push(event.latLng.toJSON());

    const formGroup = this.locationFormGroup();
    formGroup.controls['latitude'].patchValue(coords.lat);
    formGroup.controls['longitude'].patchValue(coords.lng);
    this.reverseGeocode(coords);
  }

  private sendWarningMessage(
    countryNotSupportedKey: string = 'location.ui.countrynotsupported.notification',
  ) {
    const countryNotSupported =
      !this.supportedCountryIsoEnums ||
      this.supportedCountryIsoEnums.length === 0;
    if (countryNotSupported) {
      this.ngxToastrService.warning(countryNotSupportedKey);
    } else {
      this.ngxToastrService.info('ERROR.CATALOG_BR_LOCATION_2');
    }
  }

  private setExactAddress(locationDTO: IWHLocationDTO, coords: any): boolean {
    const mk1 = {
      lat: locationDTO.latitude,
      lng: locationDTO.longitude,
    };
    const mk2 = {
      lat: coords.lat,
      lng: coords.lng,
    };
    const distance = haversineDistance(mk1, mk2);
    return distance * 1000 <= 9;
  }

  public get activeZoom(): number {
    return this.isGeoSet ? this.zoom : 8;
  }

  public get isGeoSet(): boolean {
    const formGroup = this.locationFormGroup();
    return (
      !!formGroup?.controls['longitude'].value &&
      !!formGroup?.controls['latitude'].value
    );
  }

  public get formattedAddress(): string {
    return this.locationFormGroup()?.controls['formattedAddress'].value ?? '';
  }

  public get isExact(): boolean {
    const fg = this.locationFormGroup();
    // Only meaningful once we actually have an address to be exact about.
    return !!fg?.controls['formattedAddress'].value && !!fg.controls['exact'].value;
  }

  public get latitude(): number {
    const value = this.locationFormGroup()?.controls['latitude'].value;
    return value ? +value : this.defaultLatitude;
  }

  public get longitude(): number {
    const value = this.locationFormGroup()?.controls['longitude'].value;
    return value ? +value : this.defaultLongitude;
  }

  public get locationByGeoIcon(): string {
    return WHIconENUM.LocationByGeoIcon as string;
  }

  public get locationExactIcon(): string {
    return WHIconENUM.ExactAddressPinIcon as string;
  }

  public get locationNotExactIcon(): string {
    return WHIconENUM.UnexactAddressPin as string;
  }
}
